import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/precos/reajuste-dialog.tsx — Fase 12, Plano 06 (PV-09, D-14, D-15, R12-10).
// Reajuste em massa com prévia obrigatória: configurar -> gerar prévia (todas as páginas do mesmo
// `previaHash`, virtualizadas, sem rolagem horizontal) -> aplicar exatamente o hash revisado.
// Qualquer mudança de campo, hash divergente ou 409 PREVIA_DESATUALIZADA descarta a prévia.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type {
  AlvoReajustePreco,
  AplicarReajusteInput,
  ClienteLookupPv,
  PrecoTintaVersaoView,
  PreviaReajuste,
  PreviaReajusteInput,
  ReajusteAplicadoView,
  ReajustePrecoInput,
  TipoReajustePreco,
} from '@pieltec/contracts';
import { ClienteCombobox } from '../../../components/vendas/cliente-combobox';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { FormDialogBody, FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { apiMutate } from '../../../lib/csrf-fetch';
import { formatarData, formatarMoeda, hojeIso } from '../../../lib/vendas/formatacao';
import {
  PREVIA_VAZIA,
  acumularPagina,
  descartarPrevia,
  previaValida,
  rotuloVariacao,
  type EstadoPrevia,
  type ParametrosReajuste,
} from '../../../lib/vendas/precos';
import { lerNumeroDigitado, mensagemNumeroDigitado } from '../../../lib/vendas/numero';
import { lerMensagemErro } from './nova-versao-sheet';

interface ReajusteDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly alvoInicial: AlvoReajustePreco;
  /** Chamado ao fechar depois de um reajuste aplicado, para recarregar as listas. */
  readonly onAplicado: () => void;
}

const LIMITE_PAGINA = 200;
const ALTURA_LINHA = 40;
const ALTURA_LISTA = 320;
const CODIGO_PREVIA_DESATUALIZADA = 'PREVIA_DESATUALIZADA';

function paraCorpo(p: ParametrosReajuste): ReajustePrecoInput {
  return {
    alvo: p.alvo,
    ...(p.alvo === 'SERVICO' && p.clienteIds.length > 0 ? { clienteIds: p.clienteIds } : {}),
    ...(p.alvo === 'TINTA' && p.fornecedores.length > 0 ? { fornecedores: p.fornecedores } : {}),
    tipo: p.tipo,
    valor: p.valor,
    vigenciaInicio: p.vigenciaInicio,
  };
}

export function ReajusteDialog({ aberto, onAbertoChange, alvoInicial, onAplicado }: ReajusteDialogProps) {
  const [alvo, setAlvo] = useState<AlvoReajustePreco>(alvoInicial);
  const [clientes, setClientes] = useState<readonly ClienteLookupPv[]>([]);
  const [fornecedores, setFornecedores] = useState<readonly string[]>([]);
  const [fornecedoresDisponiveis, setFornecedoresDisponiveis] = useState<readonly string[]>([]);
  const [tipo, setTipo] = useState<TipoReajustePreco>('PERCENTUAL');
  const [valorTexto, setValorTexto] = useState('');
  const [inicio, setInicio] = useState('');
  const [erroCampo, setErroCampo] = useState<string | null>(null);

  const [previa, setPrevia] = useState<EstadoPrevia>(PREVIA_VAZIA);
  const [previstos, setPrevistos] = useState<ParametrosReajuste | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<ReajusteAplicadoView | null>(null);
  const geracaoRef = useRef(0);
  const listaRef = useRef<HTMLDivElement>(null);

  const atuais: ParametrosReajuste = {
    alvo,
    clienteIds: clientes.map((c) => c.id),
    fornecedores,
    tipo,
    valor: lerNumeroDigitado(valorTexto),
    vigenciaInicio: inicio,
  };

  function invalidarPrevia(): void {
    geracaoRef.current += 1;
    setPrevia(descartarPrevia());
    setPrevistos(null);
    setCarregando(false);
  }

  /** Qualquer alteração de campo passa por aqui: a prévia anterior deixa de valer. */
  function alterar(fn: () => void): void {
    fn();
    invalidarPrevia();
    setErroApi(null);
    setErroCampo(null);
  }

  useEffect(() => {
    if (!aberto) return;
    geracaoRef.current += 1;
    setAlvo(alvoInicial);
    setClientes([]);
    setFornecedores([]);
    setTipo('PERCENTUAL');
    setValorTexto('');
    setInicio('');
    setErroCampo(null);
    setPrevia(PREVIA_VAZIA);
    setPrevistos(null);
    setCarregando(false);
    setAplicando(false);
    setErroApi(null);
    setSucesso(null);
  }, [aberto, alvoInicial]);

  useEffect(() => {
    if (!aberto || alvo !== 'TINTA') return;
    let vivo = true;
    void fetch('/api/v1/vendas/precos/tintas?historico=true', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok || !vivo) return;
        const linhas = (await res.json()) as PrecoTintaVersaoView[];
        const nomes = [...new Set(linhas.map((l) => l.fornecedorNome).filter((n): n is string => !!n))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
        if (vivo) setFornecedoresDisponiveis(nomes);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [aberto, alvo]);

  const previaVigente = previaValida({ prevista: previstos, atual: atuais, hash: previa.previaHash });
  const podeAplicar = previaVigente && previa.completa && !aplicando && !carregando;

  const virtualizador = useVirtualizer({
    count: previa.linhas.length,
    getScrollElement: () => listaRef.current,
    estimateSize: () => ALTURA_LINHA,
    overscan: 10,
  });

  const tituloPrevia = useMemo(() => {
    if (!previstos || previa.previaHash === null) return null;
    return `${previa.total} preços serão criados com início em ${formatarData(previstos.vigenciaInicio)}.`;
  }, [previa.previaHash, previa.total, previstos]);

  function validarCampos(): boolean {
    if (!Number.isFinite(atuais.valor)) {
      setErroCampo(mensagemNumeroDigitado(valorTexto, 'Informe o valor do reajuste.'));
      return false;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(inicio)) {
      setErroCampo('Informe a data de início da vigência.');
      return false;
    }
    if (inicio <= hojeIso()) {
      setErroCampo('Informe uma data futura.');
      return false;
    }
    setErroCampo(null);
    return true;
  }

  async function gerarPrevia(): Promise<void> {
    if (!validarCampos()) return;
    const geracao = geracaoRef.current + 1;
    geracaoRef.current = geracao;
    const parametros = atuais;
    setPrevia(PREVIA_VAZIA);
    setPrevistos(parametros);
    setErroApi(null);
    setCarregando(true);
    let estado: EstadoPrevia = PREVIA_VAZIA;
    let cursor: string | undefined;
    try {
      for (;;) {
        const corpo: PreviaReajusteInput = { ...paraCorpo(parametros), limite: LIMITE_PAGINA, ...(cursor ? { cursor } : {}) };
        const res = await apiMutate('/api/v1/vendas/precos/reajuste/previa', { body: corpo });
        if (geracao !== geracaoRef.current) return;
        if (!res.ok) {
          const erro = await lerMensagemErro(res, 'Não foi possível gerar a prévia.');
          if (geracao !== geracaoRef.current) return;
          setErroApi(erro.mensagem);
          invalidarPrevia();
          return;
        }
        const pagina = (await res.json()) as PreviaReajuste;
        if (geracao !== geracaoRef.current) return;
        const acumulo = acumularPagina(estado, pagina);
        if (!acumulo.ok) {
          setErroApi('Os preços mudaram enquanto a prévia carregava. Gere a prévia novamente.');
          invalidarPrevia();
          return;
        }
        estado = acumulo.estado;
        setPrevia(estado);
        if (estado.completa || estado.proximoCursor === null) break;
        cursor = estado.proximoCursor;
      }
    } catch {
      if (geracao !== geracaoRef.current) return;
      setErroApi('Não foi possível gerar a prévia. Verifique a conexão e tente novamente.');
      invalidarPrevia();
      return;
    }
    if (geracao === geracaoRef.current) setCarregando(false);
  }

  async function aplicar(): Promise<void> {
    if (!podeAplicar || !previstos || !previa.previaHash) return;
    const corpo: AplicarReajusteInput = { ...paraCorpo(previstos), previaHash: previa.previaHash };
    setAplicando(true);
    setErroApi(null);
    try {
      const res = await apiMutate('/api/v1/vendas/precos/reajuste/aplicar', { body: corpo });
      if (!res.ok) {
        const erro = await lerMensagemErro(res, 'Não foi possível aplicar o reajuste.');
        setErroApi(erro.mensagem);
        if (erro.codigo === CODIGO_PREVIA_DESATUALIZADA || res.status === 409) invalidarPrevia();
        return;
      }
      setSucesso((await res.json()) as ReajusteAplicadoView);
    } catch {
      setErroApi('Não foi possível aplicar o reajuste. Verifique a conexão e tente novamente.');
    } finally {
      setAplicando(false);
    }
  }

  function fechar(proximo: boolean): void {
    if (!proximo) {
      // WR-D1-05: com o POST de aplicar em voo o diálogo não fecha (senão o resultado e a recarga das listas se perdem).
      if (aplicando) return;
      if (sucesso) onAplicado();
      // Fechar durante "gerar prévia" invalida a geração: o laço de paginação para de chamar a API.
      else invalidarPrevia();
    }
    onAbertoChange(proximo);
  }

  const travado = aplicando || sucesso !== null;

  return (
    <FormDialog open={aberto} onOpenChange={fechar}>
      <FormDialogContent size="wide">
        <FormDialogHeader>
          <FormDialogTitle>Reajuste em massa</FormDialogTitle>
          <FormDialogDescription>PVs abertos mantêm o preço congelado. Para atualizá-los, use &quot;Reprecificar&quot; no item.</FormDialogDescription>
        </FormDialogHeader>

        <FormDialogBody>
        {sucesso ? (
          <div role="status" className="flex flex-col gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-medium">Reajuste aplicado: {sucesso.totalVersoes} novas versões criadas.</p>
            {sucesso.ignoradas > 0 && <p>{sucesso.ignoradas} preços não entraram por já terem versão com início em ou depois desta data.</p>}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <fieldset className="flex flex-col gap-2" disabled={travado}>
                <legend className="text-sm font-medium">Aplicar a</legend>
                <RadioGroup value={alvo} onValueChange={(v) => alterar(() => setAlvo(v as AlvoReajustePreco))} aria-label="Aplicar a">
                  <label className="flex items-center gap-2 text-sm" htmlFor="reajuste-alvo-servico">
                    <RadioGroupItem id="reajuste-alvo-servico" value="SERVICO" />
                    Cliente
                  </label>
                  <label className="flex items-center gap-2 text-sm" htmlFor="reajuste-alvo-tinta">
                    <RadioGroupItem id="reajuste-alvo-tinta" value="TINTA" />
                    Fornecedor da tinta
                  </label>
                </RadioGroup>
              </fieldset>

              <fieldset className="flex flex-col gap-2" disabled={travado}>
                <legend className="text-sm font-medium">Tipo</legend>
                <RadioGroup value={tipo} onValueChange={(v) => alterar(() => setTipo(v as TipoReajustePreco))} aria-label="Tipo">
                  <label className="flex items-center gap-2 text-sm" htmlFor="reajuste-tipo-percentual">
                    <RadioGroupItem id="reajuste-tipo-percentual" value="PERCENTUAL" />
                    Percentual (%)
                  </label>
                  <label className="flex items-center gap-2 text-sm" htmlFor="reajuste-tipo-valor">
                    <RadioGroupItem id="reajuste-tipo-valor" value="VALOR" />
                    Valor (R$)
                  </label>
                </RadioGroup>
              </fieldset>
            </div>

            {alvo === 'SERVICO' ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="reajuste-cliente">Clientes</Label>
                <ClienteCombobox
                  id="reajuste-cliente"
                  value={null}
                  disabled={travado}
                  onChange={(cliente) => alterar(() => setClientes((atual) => (atual.some((c) => c.id === cliente.id) ? atual : [...atual, cliente])))}
                />
                {clientes.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum cliente selecionado: o reajuste atinge todos os clientes.</p>
                ) : (
                  <ul className="flex flex-wrap gap-2" aria-label="Clientes selecionados">
                    {clientes.map((cliente) => (
                      <li key={cliente.id} className="inline-flex items-center gap-1 rounded-full border bg-slate-50 py-0.5 pl-3 pr-1 text-xs">
                        <span className="max-w-[16rem] truncate">{cliente.nome}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          disabled={travado}
                          aria-label={`Remover ${cliente.nome}`}
                          onClick={() => alterar(() => setClientes((atual) => atual.filter((c) => c.id !== cliente.id)))}
                        >
                          <X aria-hidden="true" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <fieldset className="flex flex-col gap-2" disabled={travado}>
                <legend className="text-sm font-medium">Fornecedores da tinta</legend>
                {fornecedoresDisponiveis.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum fornecedor cadastrado nos preços de tinta: o reajuste atinge todas as tintas.</p>
                ) : (
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {fornecedoresDisponiveis.map((nome, indice) => {
                      const idCampo = `reajuste-fornecedor-${indice}`;
                      return (
                        <label key={nome} htmlFor={idCampo} className="flex cursor-pointer items-center gap-2 text-sm">
                          <Checkbox
                            id={idCampo}
                            checked={fornecedores.includes(nome)}
                            onCheckedChange={(marcada) =>
                              alterar(() => setFornecedores((atual) => (marcada === true ? [...atual, nome] : atual.filter((f) => f !== nome))))
                            }
                          />
                          {nome}
                        </label>
                      );
                    })}
                  </div>
                )}
                {fornecedoresDisponiveis.length > 0 && fornecedores.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhum fornecedor marcado: o reajuste atinge todas as tintas.</p>
                )}
              </fieldset>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="reajuste-valor">Valor</Label>
                <Input
                  id="reajuste-valor"
                  inputMode="decimal"
                  placeholder={tipo === 'PERCENTUAL' ? '10' : '0,50'}
                  value={valorTexto}
                  disabled={travado}
                  onChange={(e) => alterar(() => setValorTexto(e.target.value))}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="reajuste-inicio">Vigente a partir de (data futura)</Label>
                <DatePicker id="reajuste-inicio" value={inicio} disabled={travado} onValueChange={(value) => alterar(() => setInicio(value))} />
              </div>
            </div>

            {erroCampo && <FormMessage>{erroCampo}</FormMessage>}
            {erroApi && <FormMessage>{erroApi}</FormMessage>}

            {(carregando || previa.previaHash !== null) && (
              <section className="flex flex-col gap-2" aria-label="Prévia do reajuste">
                {tituloPrevia && <h3 className="text-sm font-semibold">{tituloPrevia}</h3>}
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  {carregando ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                      {previa.previaHash === null ? 'Gerando prévia...' : `${previa.linhas.length} de ${previa.total} carregadas`}
                    </span>
                  ) : (
                    `${previa.linhas.length} de ${previa.total} carregadas`
                  )}
                  {previa.ignoradas > 0 && ` · ${previa.ignoradas} ignoradas (já têm versão com início em ou depois desta data)`}
                </p>
                {previa.linhas.length > 0 && (
                  <div className="overflow-hidden rounded-md border">
                    <div className="grid grid-cols-[minmax(0,1fr)_7rem_7rem_5rem] gap-2 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider" role="row">
                      <span>{alvo === 'SERVICO' ? 'Cliente e item' : 'Tinta'}</span>
                      <span className="text-right">Atual</span>
                      <span className="text-right">Novo</span>
                      <span className="text-right">Variação</span>
                    </div>
                    <div ref={listaRef} className="overflow-y-auto overflow-x-hidden" style={{ height: ALTURA_LISTA }} role="table" aria-label="Preços da prévia">
                      <div style={{ height: virtualizador.getTotalSize(), position: 'relative' }}>
                        {virtualizador.getVirtualItems().map((item) => {
                          const linha = previa.linhas[item.index];
                          if (!linha) return null;
                          return (
                            <div
                              key={linha.chave}
                              role="row"
                              className="absolute left-0 grid w-full grid-cols-[minmax(0,1fr)_7rem_7rem_5rem] items-center gap-2 border-t px-3 text-sm"
                              style={{ top: 0, height: item.size, transform: `translateY(${item.start}px)` }}
                            >
                              <span className="truncate" title={linha.rotulo}>
                                {linha.rotulo}
                              </span>
                              <span className="text-right tabular-nums">{formatarMoeda(linha.atual, 4)}</span>
                              <span className="text-right tabular-nums">{formatarMoeda(linha.novo, 4)}</span>
                              <span className="text-right tabular-nums">{rotuloVariacao(linha.variacao)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        </FormDialogBody>
        <FormDialogFooter>
          {sucesso ? (
            <Button type="button" onClick={() => fechar(false)}>
              Fechar
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={aplicando || carregando} onClick={() => fechar(false)}>
                Cancelar
              </Button>
              {previa.previaHash !== null && (
                <Button type="button" variant="outline" disabled={aplicando} onClick={invalidarPrevia}>
                  Voltar
                </Button>
              )}
              {previa.previaHash === null ? (
                <Button type="button" disabled={carregando} onClick={() => void gerarPrevia()}>
                  {carregando ? 'Gerando prévia...' : 'Gerar prévia'}
                </Button>
              ) : (
                <Button type="button" disabled={!podeAplicar} onClick={() => void aplicar()}>
                  {aplicando ? 'Aplicando...' : 'Aplicar reajuste'}
                </Button>
              )}
            </>
          )}
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
